import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { UserShortInfo } from "../../types";
import { backend, Dates, statsBackend } from "../../services";
import { actions } from "../../state/reducer";
import { toast } from "sonner";
import { generateRandomString } from "../../utils";
import { defaultCurrencyId, defaultSalePlan } from "../../constants";
import { SelectInput, TextInput } from "../components";

interface formData {
  user_id: number;
  dates: Dates;
  value: number;
  currency_id: number;
}

interface ModalLoaderProps {
  user: UserShortInfo;
  onSave: () => void;
  closeModal: () => void;
}

const SetupSalePlanModal: React.FC<ModalLoaderProps> = ({
  user,
  onSave,
  closeModal,
}) => {
  if (!user) {
    toast("Пользователь не выбран");
    return null;
  }
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<formData>({
    user_id: Number(user.id),
    dates: {
      month: new Date().getMonth() + 1, // Текущий месяц (1-12)
      year: new Date().getFullYear(), // Текущий год
    },
    value: defaultSalePlan,
    currency_id: defaultCurrencyId, // Пример: ID валюты по умолчанию
  });

  // Получаем текущую дату для ограничений
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // Месяцы в JS: 0-11, поэтому +1

  // Обработчик изменения месяца
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMonth = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      dates: {
        ...prev.dates,
        month: selectedMonth,
      },
    }));
  };

  // Обработчик изменения года
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedYear = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      dates: {
        ...prev.dates,
        year: selectedYear,
      },
    }));
  };

  // Обработчик изменения суммы
  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      value: value,
    }));
  };

  // Обработчик изменения валюты
  const handleCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const currencyId = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      currency_id: currencyId,
    }));
  };

  // Сохранение данных
  const saveAction = async () => {
    return new Promise((resolve) => {
      // Здесь можно добавить вызов API для сохранения данных
      statsBackend
        .setupUserSalePlan(
          formData.user_id,
          {
            ...formData.dates,
          },
          formData.value,
          formData.currency_id
        )
        .then(() => {
          console.log("Данные для сохранения:", formData);
          toast("План продаж сохранен");
          dispatch(actions.selectItem(generateRandomString(3)));
          resolve(true);
        })
        .catch(() => {
          toast("Ошибка сохранения данных");
        });
    });
  };

  // Закрытие модального окна с сохранением
  const closeWithSave = async () => {
    if (formData.value <= 0) {
      toast("Укажите сумму");
      return;
    }
    await saveAction();
    onSave(); // Передаем данные в callback
    closeModal(); // Закрываем модальное окно
  };

  // Генерация списка месяцев
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i); // Следующие 5 лет

  return (
    <div className="creationForm">
      <div className="modalHeading">
        <h3>{`План продаж - ${user.name || user.first_name || ""}`}</h3>
      </div>
      <form className="columnCenter">
        <SelectInput
          label="Месяц"
          value={formData.dates.year}
          options={months.map((month) => ({
            value: month,
            label: new Date(2023, month - 1).toLocaleString("default", {
              month: "long",
            }),
            disabled:
              formData.dates.year === currentYear && month < currentMonth,
          }))}
          onChange={handleMonthChange}
        />

        <SelectInput
          label="Год"
          value={formData.dates.year}
          options={years.map((year) => ({
            value: year,
            label: String(year),
          }))}
          onChange={handleYearChange}
        />

        {/* Поле: Валюта */}
        <SelectInput
          label="Валюта"
          value={formData.currency_id}
          options={[
            {
              value: 2,
              label: "USD",
            },
            {
              value: 1,
              label: "RUB",
            },
          ]}
          onChange={handleCurrencyChange}
        />

        {/* Поле: Сумма */}
        <TextInput
          label="Сумма"
          type="number"
          value={String(formData.value)}
          onChange={handleValueChange}
        />

        {/* Кнопки действий */}
        <div className="flex between btnRow">
          <button
            className="filterButton"
            type="button"
            onClick={closeWithSave}
          >
            Сохранить
          </button>
          <button className="cancelButton" type="button" onClick={closeModal}>
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

export default SetupSalePlanModal;
